import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';

const DAYS = [
  { n: 1 },
  { n: 2 },
  { n: 3 },
  { n: 4 },
  { n: 5 },
  { n: 6 },
  { n: 7 },
];

function toInput(value) {
  if (!value) return '09:00';
  return String(value).slice(0, 5);
}

export default function HoursPage({ embedded = false }) {
  const t = useT();
  const { isManager } = useAuth();
  const [params] = useSearchParams();
  const requestedBranch = params.get('branch') || '';
  const [branches, setBranches] = useState([]);
  const [hours, setHours] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [applyAll, setApplyAll] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    const [{ data: branchRows, error: branchError }, { data: hourRows, error: hourError }] =
      await Promise.all([
        supabase.from('branches').select('*').order('name'),
        supabase.from('branch_hours').select('*').order('weekday'),
      ]);
    if (branchError || hourError) {
      setError(branchError?.message || hourError?.message);
      return;
    }
    setBranches(branchRows || []);
    setHours(hourRows || []);
    setBranchId((current) => requestedBranch || current || branchRows?.[0]?.id || '');
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (requestedBranch) setBranchId(requestedBranch);
  }, [requestedBranch]);

  const branch = useMemo(
    () => branches.find((item) => item.id === branchId),
    [branches, branchId]
  );
  const week = useMemo(() => {
    return DAYS.map((day) => {
      const row = hours.find((item) => item.branch_id === branchId && item.weekday === day.n);
      return {
        weekday: day.n,
        label: t(`hours.day.${day.n}`),
        id: row?.id,
        opens_at: toInput(row?.opens_at),
        closes_at: toInput(row?.closes_at),
        is_closed: Boolean(row?.is_closed),
      };
    });
  }, [hours, branchId, t]);

  function patchDay(weekday, next) {
    setHours((current) => {
      const index = current.findIndex(
        (row) => row.branch_id === branchId && row.weekday === weekday
      );
      if (index === -1) {
        return [
          ...current,
          {
            branch_id: branchId,
            weekday,
            opens_at: '09:00',
            closes_at: '18:00',
            is_closed: false,
            ...next,
          },
        ];
      }
      return current.map((row, i) => (i === index ? { ...row, ...next } : row));
    });
  }

  async function upsertWeek(targetBranchId) {
    for (const day of week) {
      const payload = {
        branch_id: targetBranchId,
        weekday: day.weekday,
        opens_at: day.opens_at,
        closes_at: day.closes_at,
        is_closed: day.is_closed,
      };
      const existing = hours.find(
        (row) => row.branch_id === targetBranchId && row.weekday === day.weekday
      );
      const query = existing?.id
        ? supabase.from('branch_hours').update(payload).eq('id', existing.id)
        : supabase.from('branch_hours').upsert(payload, {
            onConflict: 'branch_id,weekday',
          });
      const { error: hourError } = await query;
      if (hourError) return hourError;
    }
    return null;
  }

  async function saveHours() {
    if (!isManager || !branch) return;
    setSaving(true);
    setError('');
    const targets = applyAll ? branches : [branch];
    const slotMinutes = Number(branch.slot_minutes) || 30;
    const parallelSlots = Math.min(2, Math.max(1, Number(branch.parallel_slots) || 2));

    for (const target of targets) {
      const { error: branchError } = await supabase
        .from('branches')
        .update({
          slot_minutes: slotMinutes,
          parallel_slots: parallelSlots,
        })
        .eq('id', target.id);
      if (branchError) {
        setSaving(false);
        setError(branchError.message);
        return;
      }
      const hourError = await upsertWeek(target.id);
      if (hourError) {
        setSaving(false);
        setError(hourError.message);
        return;
      }
    }
    setSaving(false);
    setApplyAll(false);
    load();
  }

  const saveLabel = applyAll
    ? saving
      ? t('common.saving')
      : t('hours.saveAll', { n: branches.length })
    : saving
      ? t('common.saving')
      : t('hours.saveBranch');

  const saveButton = isManager ? (
    <button type="button" className="primary" onClick={saveHours} disabled={saving || !branch}>
      {saveLabel}
    </button>
  ) : null;

  return (
    <section>
      {!embedded && (
        <header className="page-head">
          <div>
            <span className="kicker">{t('setup.kicker')}</span>
            <h1>{t('setup.hours')}</h1>
            <p className="lede">
              {t('setup.hoursLede')}
            </p>
          </div>
          {saveButton}
        </header>
      )}
      {embedded && <div className="panel-head">{saveButton}</div>}
      {error && <p className="form-error">{error}</p>}

      <div className="schedule-toolbar">
        <label>
          {t('setup.branch')}
          <select
            value={branchId}
            onChange={(e) => {
              setBranchId(e.target.value);
              setApplyAll(false);
            }}
            disabled={!branches.length}
          >
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        {isManager && branches.length > 1 && (
          <label className="check schedule-apply">
            <input
              type="checkbox"
              checked={applyAll}
              onChange={(e) => setApplyAll(e.target.checked)}
            />
            {t('hours.applyAll')}
          </label>
        )}
      </div>

      {applyAll && (
        <p className="form-error" style={{ color: 'var(--muted)' }}>
          {t('hours.applyAllWarn')}
        </p>
      )}

      {branch && (
        <div className="schedule-card">
          <h2>{t('hours.slots')}</h2>
          <p className="lede" style={{ marginTop: 0, marginBottom: 14 }}>
            {t('hours.slotsLede')}
          </p>
          <div className="form-row">
            <label>
              {t('hours.interval')}
              <input
                type="number"
                min="15"
                step="15"
                value={branch.slot_minutes ?? 30}
                disabled={!isManager}
                onChange={(e) =>
                  setBranches((current) =>
                    current.map((item) =>
                      item.id === branch.id
                        ? { ...item, slot_minutes: e.target.value }
                        : item
                    )
                  )
                }
              />
            </label>
            <label>
              {t('hours.bays')}
              <input
                type="number"
                min="1"
                max="2"
                value={branch.parallel_slots ?? 2}
                disabled={!isManager}
                onChange={(e) =>
                  setBranches((current) =>
                    current.map((item) =>
                      item.id === branch.id
                        ? { ...item, parallel_slots: e.target.value }
                        : item
                    )
                  )
                }
              />
            </label>
          </div>
        </div>
      )}

      <div className="schedule-card">
        <h2>{t('hours.opening')}</h2>
        <p className="lede" style={{ marginTop: 0, marginBottom: 14 }}>
          {t('hours.openingLede', {
            place: applyAll ? t('hours.everyBranch') : branch?.name || t('hours.thisBranch'),
          })}
        </p>
        <div className="table-wrap" style={{ boxShadow: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>{t('hours.dayCol')}</th>
                <th>{t('hours.opens')}</th>
                <th>{t('hours.closes')}</th>
                <th>{t('hours.closed')}</th>
              </tr>
            </thead>
            <tbody>
              {week.map((day) => (
                <tr key={day.weekday}>
                  <td>{day.label}</td>
                  <td>
                    <input
                      type="time"
                      value={day.opens_at}
                      disabled={!isManager || day.is_closed}
                      onChange={(e) => patchDay(day.weekday, { opens_at: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={day.closes_at}
                      disabled={!isManager || day.is_closed}
                      onChange={(e) => patchDay(day.weekday, { closes_at: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={day.is_closed}
                      disabled={!isManager}
                      onChange={(e) => patchDay(day.weekday, { is_closed: e.target.checked })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
